import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type DirectoryRealm, type DirectoryUser, type AccountAuditEntry } from '@vello/api-client';
import {
  type ColumnDef,
  type DirectoryFilter,
  DIRECTORY_FILTERS,
  decodeColumnState,
  encodeColumnState,
  defaultColumnState,
  toggleColumn,
  moveColumn,
  canMoveColumn,
  visibleColumns,
  statusForFilter,
  statusTone,
  personName,
  shownPhone,
  shownEmail,
  initialsOf,
  csvFor,
  pageRange,
  formatMarketInstant,
  marketDate,
} from '../../src/index';
import { PersonName } from '../components/person-name';
import { useScopeZone } from '../components/scope-zone';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { useAccountAuditText } from '../i18n/account-audit-text';
import { Avatar, Badge, Button, EmptyResult, EmptyState, IconButton, ListTable, MenuButton, Notice, PageHeader, SearchInput, SegmentedControl, StatCard, type Column, DrawerFooter, DrawerHeader, DrawerBody, Tabs, TabPanel, KeyValueList, InlineRow, Ink, Text, PopoverAnchor, Row, Grid, Stack, Toolbar, ColumnPicker, Timeline, type ColumnPickerItem } from '@vello/web-theme';
import { Drawer, Popover } from '@vello/web-theme';
import { Flag } from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';

/**
 * The account directory to the web listing standard (Claude Design "Admin Web - Passageiros Interativo", D319):
 * search (server-side, debounced), a segmented status filter (the country is the top-bar scope's — never a second picker),
 * a column picker (locked first column, reorder, persisted per browser), a full-width list card with hover-
 * revealed row actions, a right-hand record drawer (Resumo / Histórico), the full record page, a reason-chipped
 * confirm on suspend and an undo toast on every status change. Every row is the real directory feed (`POST /v1/ops/users/search`, D413: the search text in the body) —
 * no fabricated data; counters the platform does not have (rides, wallet) are simply not shown.
 */
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const COLUMNS: readonly ColumnDef[] = [
  { id: 'person', labelId: 'dir.col.person', width: '2fr', locked: true },
  { id: 'phone', labelId: 'dir.col.phone', width: '176px' },
  { id: 'email', labelId: 'dir.col.email', width: '1.6fr' },
  { id: 'status', labelId: 'dir.col.status', width: '.9fr' },
  { id: 'country', labelId: 'dir.col.country', width: '.6fr', defaultOff: true },
  { id: 'since', labelId: 'dir.col.since', width: '1fr' },
  { id: 'realms', labelId: 'dir.col.realms', width: '1.1fr', defaultOff: true },
  { id: 'id', labelId: 'dir.col.id', width: '1.4fr', defaultOff: true },
];

function readCols(key: string): ReturnType<typeof decodeColumnState> {
  try {
    return decodeColumnState(window.localStorage.getItem(key), COLUMNS);
  } catch {
    return defaultColumnState(COLUMNS);
  }
}

function writeCols(key: string, state: ReturnType<typeof decodeColumnState>): void {
  try {
    window.localStorage.setItem(key, encodeColumnState(state));
  } catch {
    // best-effort persistence
  }
}

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function DirectoryScreen({ realm, titleId, subtitleId, detailRoute }: { realm: DirectoryRealm; titleId: string; subtitleId: string; detailRoute: string }): ReactElement {
  const { runtime, scope } = useAdmin();
  const { navigate } = useRouter();
  const { toast, confirm, takeDirectoryQuery } = useConsole();
  const { t } = useT();
  const { fail, v } = useVocab();
  const countryName = useCountryName();
  const { zone, lang } = useScopeZone();
  const sinceDate = (iso: string): string => formatMarketInstant(iso, zone, lang, 'date');
  const colsKey = `vello.admin.directory.${realm.toLowerCase()}.cols`;

  const [search, setSearch] = useState(() => takeDirectoryQuery() ?? '');
  const query = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);
  const [filter, setFilter] = useState<DirectoryFilter>('all');
  const [offset, setOffset] = useState(0);
  const [users, setUsers] = useState<readonly DirectoryUser[] | undefined>(undefined);
  const [total, setTotal] = useState(0);
  // D384 review (W5S-01): a masked directory searches names only; the server says which, the placeholder follows.
  const [contactSearch, setContactSearch] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const [cols, setCols] = useState(() => readCols(colsKey));
  const [colsOpen, setColsOpen] = useState(false);

  const [drawerUser, setDrawerUser] = useState<DirectoryUser | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'summary' | 'history'>('summary');
  const [audit, setAudit] = useState<readonly AccountAuditEntry[] | undefined>(undefined);

  // D580: the top-bar scope fixes the country (it is the one market selector); the list follows it, no second picker.
  const effectiveCountry = scope.current.country;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    const status = statusForFilter(filter);
    void runtime.adminUsers
      .list({ realm, ...(effectiveCountry !== undefined ? { country: effectiveCountry } : {}), ...(status !== undefined ? { status } : {}), ...(query !== '' ? { search: query } : {}), limit: PAGE_SIZE, offset })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setUsers(res.value.users);
          setTotal(res.value.total);
          setContactSearch(res.value.contactSearch !== false);
        } else {
          setUsers([]);
          setTotal(0);
          setError(t('dir.error', { code: fail(res.error) }));
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runtime, realm, effectiveCountry, filter, query, offset, refresh, t]);

  const saveCols = (next: ReturnType<typeof decodeColumnState>): void => {
    setCols(next);
    writeCols(colsKey, next);
  };

  const openDrawer = useCallback(
    async (u: DirectoryUser, tab: 'summary' | 'history'): Promise<void> => {
      setDrawerUser(u);
      setDrawerTab(tab);
      setDrawerOpen(true);
      setAudit(undefined);
      const res = await runtime.adminUsers.audit(u.id);
      setAudit(res.ok ? res.value.entries : []);
    },
    [runtime],
  );

  const applyStatus = useCallback(
    async (u: DirectoryUser, target: 'ACTIVE' | 'SUSPENDED', reason: string | undefined, silent: boolean): Promise<boolean> => {
      const res = await runtime.adminUsers.setStatus(u.id, target, reason);
      if (!res.ok) {
        toast({ message: t('dir.actionFailed', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
        return false;
      }
      setRefresh((n) => n + 1);
      setDrawerUser((d) => (d?.id === u.id ? { ...d, status: target } : d));
      if (drawerUser?.id === u.id) {
        const trail = await runtime.adminUsers.audit(u.id);
        setAudit(trail.ok ? trail.value.entries : []);
      }
      if (!silent) {
        const first = personName(u, effectiveCountry).split(' ')[0] ?? '';
        toast({
          message: t(target === 'SUSPENDED' ? 'dir.toast.suspended' : 'dir.toast.reactivated', { name: first }),
          icon: target === 'SUSPENDED' ? 'shieldAlert' : 'check',
          tone: target === 'SUSPENDED' ? 'warning' : 'success',
          onUndo: () => {
            void applyStatus(u, target === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED', t('dir.undoReason'), true).then((ok) => {
              if (ok) toast({ message: t('dir.toast.undone'), icon: 'undo', tone: 'neutral' });
            });
          },
        });
      }
      return true;
    },
    [runtime, toast, t, drawerUser],
  );

  const suspend = async (u: DirectoryUser): Promise<void> => {
    const name = personName(u, effectiveCountry);
    const r = await confirm({
      title: t('dir.confirm.suspend.title', { name }),
      body: t('dir.confirm.suspend.body'),
      confirmLabel: t('dir.confirm.suspend.confirm'),
      cancelLabel: t('ui.cancel'),
      icon: 'pause',
      reasons: [t('dir.reason.chargeback'), t('dir.reason.driverReport'), t('dir.reason.couponAbuse'), t('dir.reason.customerRequest')],
      reasonLabel: t('ui.reason'),
      notePlaceholder: t('dir.confirm.note'),
    });
    if (!r.confirmed) return;
    await applyStatus(u, 'SUSPENDED', r.note !== undefined ? `${r.reason ?? ''} — ${r.note}` : r.reason, false);
  };

  const reactivate = (u: DirectoryUser): void => {
    void applyStatus(u, 'ACTIVE', undefined, false);
  };

  const exportCsv = (): void => {
    const visible = visibleColumns(cols, COLUMNS);
    const rows = (users ?? []).map((u) => visible.map((c) => cellText(u, c.id)));
    const csv = csvFor(visible.map((c) => t(c.labelId)), rows);
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${realm.toLowerCase()}-${marketDate(new Date(), zone)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ message: t('dir.toast.exported', { count: String(rows.length) }), icon: 'download', tone: 'neutral' });
  };

  const cellText = (u: DirectoryUser, id: string): string => {
    switch (id) {
      case 'person':
        return personName(u, effectiveCountry);
      case 'phone':
        return shownPhone(u, effectiveCountry);
      case 'email':
        return shownEmail(u) ?? '';
      case 'status':
        return v('identity.user_status', u.status);
      case 'country':
        return u.countryCode;
      case 'since':
        return sinceDate(u.createdAt);
      case 'realms':
        return u.realms.map((x) => v('identity.realm', x)).join(', ');
      default:
        return u.id;
    }
  };

  const visible = visibleColumns(cols, COLUMNS);
  const columns: readonly Column<DirectoryUser>[] = visible.map((c) => ({
    key: c.id,
    header: t(c.labelId),
    width: c.width,
    render: (u: DirectoryUser): ReactElement | string => {
      switch (c.id) {
        case 'person':
          return (
            <InlineRow gap={3} align="center">
              <Avatar initials={initialsOf(personName(u, effectiveCountry), '·')} size={32} />
              <Ink strong truncate>{personName(u, effectiveCountry)}</Ink>
            </InlineRow>
          );
        case 'phone':
          return <Text role="caption" mono>{shownPhone(u, effectiveCountry)}</Text>;
        case 'email':
          return <Ink tone="secondary">{shownEmail(u) ?? '—'}</Ink>;
        case 'status':
          return <Badge tone={statusTone(u.status)}>{v('identity.user_status', u.status)}</Badge>;
        case 'country':
          return (
            <InlineRow gap={2} align="center">
              <Flag code={u.countryCode} />
              <span>{countryName(u.countryCode)}</span>
            </InlineRow>
          );
        case 'since':
          return <Text role="caption" tone="secondary" mono>{sinceDate(u.createdAt)}</Text>;
        case 'realms':
          return <Ink tone="secondary">{u.realms.map((x) => v('identity.realm', x)).join(' · ')}</Ink>;
        default:
          return <Text role="caption" tone="secondary" mono>{u.id}</Text>;
      }
    },
  }));

  // D387: each control of the picker row is a 48px box (the 18px square and the 26px arrows are drawn inside it).
  const pickerItems: ColumnPickerItem[] = cols.flatMap((c) => {
    const def = COLUMNS.find((x) => x.id === c.id);
    return def === undefined ? [] : [{ id: c.id, label: t(def.labelId), on: c.on, locked: def.locked === true, canMoveUp: canMoveColumn(cols, COLUMNS, c.id, -1), canMoveDown: canMoveColumn(cols, COLUMNS, c.id, 1) }];
  });
  const range = pageRange(offset, PAGE_SIZE, total);
  const filtersActive = search !== '' || filter !== 'all';
  const resetFilters = (): void => {
    setSearch('');
    setFilter('all');
    setOffset(0);
  };
  const d = drawerUser;

  return (
    <>
      <PageHeader
        title={t(titleId)}
        subtitle={t(subtitleId)}
        actions={
          <>
            <PopoverAnchor>
              <MenuButton label={t('dir.columns')} icon="columns" count={`${visible.length}/${COLUMNS.length}`} open={colsOpen} active={colsOpen} chevron={false} onClick={() => setColsOpen((o) => !o)} />
              <Popover open={colsOpen} onClose={() => setColsOpen(false)} width={300} top={54} role="dialog" ariaLabel={t('dir.columns.title')}>
                <ColumnPicker
                  title={t('dir.columns.title')}
                  resetLabel={t('dir.columns.reset')}
                  moveUpLabel={t('dir.columns.up')}
                  moveDownLabel={t('dir.columns.down')}
                  note={t('dir.columns.note')}
                  items={pickerItems}
                  onToggle={(id) => saveCols(toggleColumn(cols, COLUMNS, id))}
                  onMove={(id, dir) => saveCols(moveColumn(cols, COLUMNS, id, dir))}
                  onReset={() => saveCols(defaultColumnState(COLUMNS))}
                />
              </Popover>
            </PopoverAnchor>
            <Button variant="secondary" icon="download" onClick={exportCsv} disabled={(users ?? []).length === 0}>
              {t('dir.export')}
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchInput id="dir-search" value={search} onChange={(v) => { setSearch(v); setOffset(0); }} onClear={() => { setSearch(''); setOffset(0); }} placeholder={t(contactSearch ? 'dir.search.placeholder' : 'dir.search.placeholderName')} clearLabel={t('ui.clear')} />
        <SegmentedControl options={DIRECTORY_FILTERS.map((f) => ({ id: f, label: t(`dir.filter.${f}`) }))} value={filter} onChange={(f) => { setFilter(f); setOffset(0); }} ariaLabel={t('dir.col.status')} />
      </Toolbar>

      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      <ListTable
        columns={columns}
        rows={users ?? []}
        rowKey={(u) => u.id}
        minWidth={1080}
        onRowClick={(u) => void openDrawer(u, 'summary')}
        selectedKey={drawerOpen ? d?.id : undefined}
        actionsWidth="max-content"
        countLabel={loading ? t('ui.loading') : t('dir.count', { count: String(total) })}
        staggerKey={`${query}|${filter}|${effectiveCountry ?? ''}|${offset}|${refresh}`}
        actions={(u) => (
          <>
            <IconButton icon="userSearch" label={t('dir.openRecord')} outlined size={14} onClick={() => navigate(detailRoute, { id: u.id })} />
            <Button size="sm" variant="secondary" onClick={() => void openDrawer(u, 'history')}>{t('dir.history')}</Button>
            {u.status === 'ACTIVE' && <Button size="sm" variant="danger" onClick={() => void suspend(u)}>{t('dir.suspend')}</Button>}
            {u.status === 'SUSPENDED' && <Button size="sm" variant="success" onClick={() => reactivate(u)}>{t('dir.reactivate')}</Button>}
          </>
        )}
        empty={
          loading ? (
            <LoadingBlock />
          ) : (
            <EmptyResult title={t('dir.empty.title')} detail={filtersActive ? t('dir.empty.filters') : t('dir.empty.none')} action={filtersActive ? <Button size="sm" variant="secondary" onClick={resetFilters}>{t('dir.empty.reset')}</Button> : undefined} />
          )
        }
        pagination={{
          ...range,
          onPrev: () => setOffset(Math.max(0, offset - PAGE_SIZE)),
          onNext: () => setOffset(offset + PAGE_SIZE),
          prevLabel: t('dir.prev'),
          nextLabel: t('dir.next'),
          rangeLabel: t('dir.range', { from: String(range.from), to: String(range.to), total: String(range.total) }),
        }}
      />

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="dir-drawer-title">
        {d !== undefined && (
          <>
            <DrawerHeader titleId="dir-drawer-title" title={personName(d, effectiveCountry)} actions={<Badge tone={statusTone(d.status)}>{v('identity.user_status', d.status)}</Badge>} onClose={() => setDrawerOpen(false)} closeLabel={t('ui.close')}>
            <Stack gap={3}>
              <Row gap={3} align="center">
                <Avatar initials={initialsOf(personName(d, effectiveCountry), '·')} size={52} variant="inverse" />
                <Text as="div" role="caption" tone="secondary">
                  <Row gap={2} align="center" wrap>
                    <span className="tnum">{shownPhone(d, effectiveCountry)}</span>
                    {shownEmail(d) !== undefined && (
                      <>
                        <span aria-hidden="true">·</span>
                        <Ink breakAnywhere>{shownEmail(d)}</Ink>
                      </>
                    )}
                  </Row>
                </Text>
              </Row>
              <Tabs
                id="dir-drawer"
                ariaLabel={t('dir.drawer.tabs')}
                value={drawerTab}
                onChange={setDrawerTab}
                tabs={[
                  { id: 'summary', label: t('dir.tab.summary') },
                  { id: 'history', label: t('dir.tab.history') },
                ]}
              />
            </Stack>
            </DrawerHeader>
            <DrawerBody>
              <DrawerStack>
                <Grid columns={3} gap={3}>
                  <StatCard label={t('dir.col.country')} value={d.countryCode} />
                  <StatCard label={t('dir.col.since')} value={formatMarketInstant(d.createdAt, zone, lang, 'date-short')} />
                  <StatCard label={t('dir.col.realms')} value={String(d.realms.length)} hint={d.realms.map((x) => v('identity.realm', x)).join(' · ')} subtle />
                </Grid>
                <Button variant="secondary" block icon="arrowRight" onClick={() => navigate(detailRoute, { id: d.id })}>
                  {t('dir.openRecord.full')}
                </Button>
                <TabPanel id="dir-drawer" tab="summary" value={drawerTab}>
                  <KeyValueList
                    items={[
                      { label: t('dir.col.country'), value: d.countryCode, mono: true },
                      { label: t('dir.col.since'), value: sinceDate(d.createdAt), mono: true },
                      { label: t('dir.col.phone'), value: shownPhone(d, effectiveCountry), mono: true },
                      { label: t('dir.col.email'), value: shownEmail(d) ?? '—' },
                      { label: t('dir.col.realms'), value: d.realms.map((x) => v('identity.realm', x)).join(' · ') },
                      { label: t('dir.col.id'), value: d.id, mono: true },
                    ]}
                  />
                </TabPanel>
                <TabPanel id="dir-drawer" tab="history" value={drawerTab}>
                  <AuditTimeline entries={audit} />
                </TabPanel>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {d.status === 'ACTIVE' && <Button variant="danger" block icon="pause" onClick={() => void suspend(d)}>{t('dir.suspend.account')}</Button>}
              {d.status === 'SUSPENDED' && <Button variant="success" block icon="rotateCcw" onClick={() => reactivate(d)}>{t('dir.reactivate.account')}</Button>}
              {d.status === 'DELETED' && <Notice tone="neutral">{t('dir.deleted.note')}</Notice>}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}

/** The account-action history as a vertical timeline (real m0099 audit rows, newest first). */
export function AuditTimeline({ entries }: { entries: readonly AccountAuditEntry[] | undefined }): ReactElement {
  const { t } = useT();
  const { zone, lang } = useScopeZone();
  if (entries === undefined) return <LoadingBlock lines={4} />;
  if (entries.length === 0) return <EmptyState title={t('dir.audit.empty')} />;
  const auditText = useAccountAuditText();
  return (
    <Timeline
      entries={entries.map((e) => ({
        id: e.id,
        tone: e.action.startsWith('suspend') ? 'warning' : e.action.startsWith('payout_reject') ? 'danger' : 'neutral',
        title: auditText.action(e),
        ...(auditText.detail(e) !== undefined ? { detail: auditText.detail(e) as string } : {}),
        meta: <>{formatMarketInstant(e.at, zone, lang)} · <PersonName id={e.actorId} /></>,
      }))}
    />
  );
}
